import React, { PropsWithChildren } from 'react'
import { Theme } from '@mui/material'
import { darken, transparentize } from 'polished'
import styled from 'styled-components'
import { themeKey } from '~/common/theme-key'

import {
  BORDER_RADIUS,
  COLORS,
  COLORS2,
  EASING,
  SIZES,
} from '~/common/globalStyles'

import ButtonBase, { Props as BaseButtonProps } from './ButtonBase'

export type ButtonColors =
  | 'gray'
  | 'lightGray'
  | 'negative'
  | 'neutral'
  | 'positive'
  | 'primary'
  | 'newPrimary'
  | 'purple'
  | 'secondary'
  | 'mediumBlue'
  | 'lightRed'

export interface Props extends BaseButtonProps {
  color?: ButtonColors
  large?: boolean
  showBackgroundColorWithoutHover?: boolean
  size?: 'small' | 'medium' | 'large' | 'tiny'
  raised?: boolean
  bordered?: boolean
  theme?: Theme
  onClick?: any
  innerRef?: React.Ref<HTMLButtonElement | null>
}

type StyledButtonProps = PropsWithChildren<Required<Omit<Props, 'children'>>>

type ButtonVariantProps = {
  theme: Theme & { name: string }
  color?: ButtonColors
}

type ButtonThemeValue = string | ((props: ButtonVariantProps) => string)

type ButtonThemeMap = {
  default: ButtonThemeValue
  [themeName: string]: ButtonThemeValue
}

const resolveButtonThemeValue = (
  value: ButtonThemeValue,
  props: ButtonVariantProps,
) => (typeof value === 'function' ? value(props) : value)

const createButtonVariantResolver =
  (variants: Record<ButtonColors, ButtonThemeMap>) =>
  (props: ButtonVariantProps) => {
    const color = props.color ?? 'primary'
    const values = variants[color]
    const value = values[props.theme.name] ?? values.default

    return resolveButtonThemeValue(value, props)
  }

const buttonBackgroundColorRaisedVariants = {
  primary: {
    default: themeKey('palette.current'),
    lightV2: themeKey('palette.current'),
    dark: themeKey('palette.current'),
  },
  newPrimary: {
    default: COLORS2.ash[500],
    lightV2: themeKey('palette.primary.main'),
  },
  secondary: {
    default: COLORS.lightBlue,
    lightV2: themeKey('palette.sky[500]'),
    dark: COLORS.darkBlue,
  },
  positive: { default: COLORS.green, lightV2: themeKey('palette.green[500]') },
  negative: { default: COLORS.red, lightV2: themeKey('palette.red[500]') },
  neutral: { default: COLORS.white, lightV2: COLORS.white },
  purple: { default: COLORS.purple, lightV2: themeKey('palette.purple[500]') },
  gray: { default: COLORS.mediumGray, lightV2: themeKey('palette.grey[500]') },
  lightGray: {
    default: COLORS.lightGray,
    lightV2: themeKey('palette.grey[300]'),
  },
  mediumBlue: { default: COLORS.mediumBlue, lightV2: COLORS.mediumBlue },
  lightRed: { default: COLORS.lightRed, lightV2: themeKey('palette.red[300]') },
} as Record<ButtonColors, ButtonThemeMap>

const buttonBackgroundColorRaised = createButtonVariantResolver(
  buttonBackgroundColorRaisedVariants,
)

const buttonHoverBackgroundColorRaisedVariants = {
  primary: {
    default: (props) => darken(0.01, themeKey('palette.current')(props)),
    lightV2: (props) => darken(0.01, themeKey('palette.current')(props)),
  },
  newPrimary: {
    default: darken(0.01, COLORS2.ash[500]),
    lightV2: themeKey('palette.primary.main'),
  },
  secondary: {
    default: darken(0.01, COLORS.lightBlue),
    lightV2: (props) => darken(0.01, themeKey('palette.sky[500]')(props)),
  },
  positive: {
    default: darken(0.1, COLORS.green),
    lightV2: (props) => darken(0.01, themeKey('palette.green[500]')(props)),
  },
  negative: {
    default: darken(0.1, COLORS.red),
    lightV2: (props) => darken(0.01, themeKey('palette.red[500]')(props)),
  },
  neutral: { default: COLORS.lightestGray, lightV2: COLORS.lightestGray },
  purple: {
    default: darken(0.1, COLORS.purple),
    lightV2: (props) => darken(0.01, themeKey('palette.purple[500]')(props)),
  },
  gray: {
    default: darken(0.1, COLORS.mediumGray),
    lightV2: (props) => darken(0.01, themeKey('palette.grey[500]')(props)),
  },
  lightGray: {
    default: darken(0.1, COLORS.lightGray),
    lightV2: (props) => darken(0.01, themeKey('palette.grey[300]')(props)),
  },
  mediumBlue: {
    default: darken(0.1, COLORS.mediumBlue),
    lightV2: darken(0.1, COLORS.mediumBlue),
  },
  lightRed: {
    default: darken(0.1, COLORS.lightRed),
    lightV2: (props) => darken(0.01, themeKey('palette.red[300]')(props)),
  },
} as Record<ButtonColors, ButtonThemeMap>

const buttonHoverBackgroundColorRaised = createButtonVariantResolver(
  buttonHoverBackgroundColorRaisedVariants,
)

const buttonHoverBackgroundColorFlatVariants = {
  primary: {
    default: transparentize(0.95, COLORS.blue),
    lightV2: (props) =>
      transparentize(0.95, themeKey('palette.current')(props)),
  },
  newPrimary: {
    default: transparentize(0.95, COLORS2.ash[500]),
    lightV2: (props) =>
      transparentize(0.95, themeKey('palette.primary.main')(props)),
  },
  secondary: {
    default: transparentize(0.95, COLORS.lightBlue),
    lightV2: (props) =>
      transparentize(0.95, themeKey('palette.sky[500]')(props)),
  },
  positive: {
    default: transparentize(0.95, COLORS.green),
    lightV2: (props) =>
      transparentize(0.95, themeKey('palette.green[500]')(props)),
  },
  negative: {
    default: transparentize(0.95, COLORS.red),
    lightV2: (props) =>
      transparentize(0.95, themeKey('palette.red[500]')(props)),
  },
  neutral: {
    default: transparentize(0.95, COLORS.darkGray),
    lightV2: transparentize(0.95, COLORS.darkGray),
  },
  purple: {
    default: transparentize(0.95, COLORS.purple),
    lightV2: (props) =>
      transparentize(0.95, themeKey('palette.purple[500]')(props)),
  },
  gray: {
    default: transparentize(0.95, COLORS.mediumGray),
    lightV2: (props) =>
      transparentize(0.95, themeKey('palette.grey[500]')(props)),
  },
  lightGray: {
    default: transparentize(0.96, COLORS.mediumGray),
    lightV2: (props) =>
      transparentize(0.95, themeKey('palette.grey[300]')(props)),
  },
  mediumBlue: {
    default: transparentize(0.96, COLORS.mediumBlue),
    lightV2: transparentize(0.96, COLORS.mediumBlue),
  },
  lightRed: {
    default: transparentize(0.96, COLORS.lightRed),
    lightV2: (props) =>
      transparentize(0.95, themeKey('palette.red[300]')(props)),
  },
} as Record<ButtonColors, ButtonThemeMap>

const buttonHoverBackgroundColorFlat = createButtonVariantResolver(
  buttonHoverBackgroundColorFlatVariants,
)

const calculateFlatButtonColor = (
  color: ButtonColors = 'neutral',
  theme: Theme | undefined,
) => {
  const flatButtonColorMap = {
    gray: `${theme?.palette.grey[500]}` || COLORS.mediumGray,
    lightGray: `${theme?.palette.grey[100]}` || COLORS.lightGray,
    negative: `${theme?.palette.red[500]}` || COLORS.red,
    neutral: `${theme?.palette.grey[800]}` || COLORS.darkGray,
    positive: `${theme?.palette.green[500]}` || COLORS.green,
    primary: `${theme?.palette.grey[500]}` || COLORS.mediumGray,
    newPrimary: `${theme?.palette.primary.main}` || COLORS2.ash[500],
    purple: `${theme?.palette.purple[500]}` || COLORS.purple,
    secondary: `${theme?.palette.sky[500]}` || COLORS.lightBlue,
    mediumBlue: `${theme?.palette.glacier[500]}` || COLORS.mediumBlue,
    lightRed: `${theme?.palette.red[300]}` || COLORS.lightRed,
  }
  return `${flatButtonColorMap[color]}`
}

const buttonColor = ({
  disabled,
  color,
  raised,
  theme: currentTheme,
}: Props) => {
  if (disabled) return COLORS.mediumGray
  if (raised && color === 'neutral')
    return currentTheme ? currentTheme.palette.text.secondary : COLORS.darkGray
  if (raised) return COLORS.white

  if (!raised && color === 'primary') return currentTheme?.palette.current

  return calculateFlatButtonColor(color || 'neutral', currentTheme)
}

const StyledButton = styled(ButtonBase)<StyledButtonProps>(
  ({
    theme: globalTheme,
    large,
    size,
    showBackgroundColorWithoutHover,
    disabled,
    raised,
    color,
    bordered,
  }) => `
  && {
    width: 100%;
    min-height: ${large ? SIZES.huge : SIZES.xxl};
    padding: 0 ${
      globalTheme.name === 'lightV2'
        ? globalTheme.spacing(1)
        : globalTheme.spacing(3)
    };
    border-radius: ${BORDER_RADIUS}px;
    color: ${buttonColor({
      color,
      large,
      showBackgroundColorWithoutHover,
      size,
      raised,
      bordered,
      theme: globalTheme,
    })};
    font-weight: 500;
    font-size: ${SIZES[size]};
    transition: all 300ms ${EASING.easeInOut};
    box-sizing: border-box;
    letter-spacing: 0.015rem;

    &:focus {
      outline: none;
    }

    &:hover {
      background-color: ${buttonHoverBackgroundColorFlat({
        theme: globalTheme,
        color,
      })};
    }

    ${
      showBackgroundColorWithoutHover
        ? `
        background-color: ${buttonHoverBackgroundColorFlat({
          theme: globalTheme,
          color,
        })};
      `
        : ''
    }

    ${
      !disabled && raised
        ? `background-color: ${buttonBackgroundColorRaised({
            theme: globalTheme,
            color,
          })};
        box-shadow: 0 1px 3px 0 ${globalTheme.palette.action.disabled};
        &:hover {
          background-color: ${buttonHoverBackgroundColorRaised({
            theme: globalTheme,
            color,
          })};
        }
        &:focus {
          box-shadow: 0 0 0 2px #fff, 0 0 0 4px ${globalTheme.palette.current};
        }
      `
        : ''
    }

    ${
      color === 'neutral'
        ? `
        border: 1px solid ${COLORS.lightGray};
      `
        : ''
    }

    ${
      disabled
        ? `
        background-color: none;
        cursor: none;
        color: ${COLORS.lightGray};
      `
        : ''
    }

    ${
      bordered
        ? `
        border: 1px solid ${buttonColor({
          color,
          large,
          showBackgroundColorWithoutHover,
          size,
          raised,
          bordered,
          theme: globalTheme,
        })};
      `
        : ''
    }
  }
`,
)

const Button = React.forwardRef<HTMLButtonElement, Props>(
  (
    {
      children,
      color = 'primary',
      disabled = false,
      large = false,
      raised = false,
      showBackgroundColorWithoutHover = false,
      size = 'medium',
      type = 'button',
      bordered = false,
      innerRef,
      ...props
    },
    ref,
  ) => {
    return (
      <StyledButton
        ref={innerRef ?? ref}
        {...(props as any)}
        {...{
          color,
          disabled,
          large,
          raised,
          showBackgroundColorWithoutHover,
          size,
          type,
          bordered,
        }}
      >
        {children}
      </StyledButton>
    )
  },
)

Button.displayName = 'Button'

export default Button
